import {
  AssistantCitation,
  KnowledgeAskCitation,
  WebCitation,
} from '@digital-twins/shared-types'
import type {
  AssistantAnswerAnchorV2,
  AssistantCalculationEvidence,
  AssistantConnectorRecordEvidence,
  AssistantEvidenceDoneFields,
  AssistantLibraryEvidence,
  AssistantPageRegion,
  AssistantPageRegionEvidence,
  AssistantRoomEvidence,
  AssistantSocialEvidence,
  AssistantStudyEvidence,
  AssistantWebEvidence,
} from '@digital-twins/shared-types/assistant'

import { httpsImageSrc } from '@/components/citations/citationSource'

export type EvidenceCitationTarget =
  | { kind: 'document'; sourceKind: 'library' | 'social'; citation: KnowledgeAskCitation; documentIndex: number;
      evidence: AssistantLibraryEvidence | AssistantSocialEvidence }
  | { kind: 'web'; sourceKind: 'web'; citation: AssistantWebEvidence }
  /** P1: a figure cited to the calculation that produced it. No document chip. */
  | { kind: 'calculation'; sourceKind: 'calculation'; evidence: AssistantCalculationEvidence }
  /** A region of a Library page; opens like a library citation, with its region drawn. */
  | { kind: 'page_region'; sourceKind: 'page_region'; evidence: AssistantPageRegionEvidence;
      citation: ThreadRegionCitation }
  /** A finding of one of the org's studies; opens the study results. No document chip. */
  | { kind: 'study'; sourceKind: 'study'; evidence: AssistantStudyEvidence }
  /** A twin's line in one of the org's rooms; opens the chat room. No document chip. */
  | { kind: 'room'; sourceKind: 'room'; evidence: AssistantRoomEvidence }
  /** One record from a connected CRM or file store. No document chip. */
  | { kind: 'connector_record'; sourceKind: 'connector_record'; evidence: AssistantConnectorRecordEvidence }

/**
 * A `page_region` evidence item as the document viewer's citation shape.
 * Frontend-local: the shared `KnowledgeAskCitation` is not widened, readers
 * pick `region` up through a narrow type guard.
 */
export type ThreadRegionCitation = KnowledgeAskCitation & { region?: AssistantPageRegion }

function isFraction(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1
}

/** True for a citation that carries a usable page region. */
export function hasRegion(citation: KnowledgeAskCitation | null | undefined): citation is ThreadRegionCitation & { region: AssistantPageRegion } {
  if (!citation || !('region' in citation)) {
    return false
  }
  const region = (citation as ThreadRegionCitation).region
  return Boolean(region) && isFraction(region?.x) && isFraction(region?.y) &&
    isFraction(region?.width) && isFraction(region?.height)
}

function regionCitation(evidence: AssistantPageRegionEvidence): ThreadRegionCitation {
  return {
    documentToken: evidence.documentToken,
    // Never the token as a name (D13): an untitled region whose document is not
    // in the index falls back to the generic source label in `citationLabel`.
    documentName: evidence.title ?? '',
    snippet: evidence.snippet ?? '',
    granularity: 'page',
    extractionVersion: '',
    pageNumber: evidence.page,
    pageUnknown: false,
    region: evidence.region,
  }
}

/** Original resolved indexes are retained, including holes for missing destinations. */
export interface PartitionedEvidenceV2 {
  targets: Array<EvidenceCitationTarget | null>
  answerSpans: Array<AssistantAnswerAnchorV2>
  /** Existing document slots map to the original mixed evidence chip number. */
  documentDisplayIndexes: Array<number | null>
}

/**
 * Either citation species a turn's `citations` array can hold.
 *
 * `KnowledgeAskCitation` is what the legacy grounded ask persists (no `kind`
 * field at all). `AssistantCitation` is what an assistant turn persists,
 * tagged `kind: 'document' | 'web'` — `KnowledgeThreadMessage.citations` in
 * `apps/frontend/src/lib/api/client.ts` is typed as exactly this union,
 * because the server writes both shapes into the same jsonb column. A reader
 * must partition on `kind` before handing citations to any renderer: a web
 * citation put through the document pipeline has no document name, no
 * anchors and no page.
 */
export type AnyThreadCitation = KnowledgeAskCitation | AssistantCitation

export interface PartitionedAssistantCitations {
  /**
   * Legacy `KnowledgeAskCitation` citations AND `kind: 'document'` assistant
   * citations, indistinguishable once partitioned: `buildAssistantCitations`
   * (`apps/backend/src/knowledge/knowledge.service.ts`) enriches a `document`
   * citation through the SAME `enrichGroundedCitations` + `insertCitations`
   * path a grounded ask uses before tagging it `kind: 'document'`, so the
   * object on the wire carries every `KnowledgeAskCitation` field
   * (`extractionVersion`, `charStart`, `unitCharStart`, `citationToken`, …)
   * even though the shared `DocumentCitation` type only declares the handful
   * of fields unique to the wire contract. Casting here is honest, not a
   * widening: it lets every existing document renderer
   * (`groupCitationsByDocument`, `injectCitationMarkers`, `ThreadAnswerBody`,
   * `ThreadSourceCard`, `ThreadSourceView`) keep taking
   * `Array<KnowledgeAskCitation>` completely unchanged.
   *
   * Legacy citations use dense 1..N numbering. V2 retains this document-only
   * array for existing viewers, with an explicit display-index mapping back
   * to the mixed resolved evidence list. Legacy numbering stays unchanged.
   */
  documentCitations: Array<KnowledgeAskCitation>
  /**
   * `kind: 'web'` citations, in their original relative order, rendered as
   * URL-keyed cards instead of entering document renderers. V2 additionally
   * anchors web chips through its separate resolved evidence metadata.
   */
  webCitations: Array<ThreadWebCitation>
  v2?: PartitionedEvidenceV2
}

function isCitationLike(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

/** sha256 hex of app plus remote id. Anything else is not a citable record key. */
const CONNECTOR_RECORD_KEY_RE = /^[0-9a-f]{64}$/

function isHttpsRecordUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

/** True for a `kind: 'web'` assistant citation. */
export function isWebAssistantCitation(
  citation: AnyThreadCitation,
): citation is WebCitation & { kind: 'web' } {
  return isCitationLike(citation) && citation.kind === 'web'
}

/**
 * True for everything the existing document pipeline can render: a legacy
 * `KnowledgeAskCitation` (no `kind` at all) or a `kind: 'document'` assistant
 * citation. Mirrors the backend's own split
 * (`documentRaw = raw.filter((c) => c.kind !== 'web')` in
 * `knowledge.service.ts:buildAssistantCitations`) so a future third `kind`
 * falls through to the same bucket on both sides of the wire instead of
 * silently vanishing on just one.
 */
export function isDocumentAssistantCitation(
  citation: AnyThreadCitation,
): citation is KnowledgeAskCitation {
  return isCitationLike(citation) && !isWebAssistantCitation(citation)
}

/**
 * A web citation as the thread renders it: the wire fields plus the page's
 * https preview image when one is known. `imageUrl` never comes from the
 * legacy citation list in practice; v2 turns backfill it from the matching
 * resolved web evidence item (see `withWebEvidenceImages`).
 */
export interface ThreadWebCitation extends WebCitation {
  imageUrl?: string
}

function toWebCitation(citation: WebCitation & { kind: 'web' }): ThreadWebCitation {
  const url = typeof citation.url === 'string' ? citation.url.trim() : ''
  const rawTitle = typeof citation.title === 'string' ? citation.title.trim() : ''
  const publisher =
    typeof citation.publisher === 'string' && citation.publisher.trim()
      ? citation.publisher.trim()
      : undefined
  const publishedAt =
    typeof citation.publishedAt === 'string' && citation.publishedAt.trim()
      ? citation.publishedAt.trim()
      : undefined

  const snippet =
    typeof citation.snippet === 'string' && citation.snippet.trim()
      ? citation.snippet.trim()
      : undefined
  const imageUrl = httpsImageSrc((citation as ThreadWebCitation).imageUrl) ?? undefined

  return {
    url,
    // A titleless web hit still needs SOMETHING to render and to group by;
    // the url is the only field guaranteed present on a real web citation.
    title: rawTitle || url,
    publisher,
    publishedAt,
    ...(snippet ? { snippet } : {}),
    ...(imageUrl ? { imageUrl } : {}),
  }
}

/**
 * V2: the legacy web citation list carries only url/title/publisher/date; the
 * page's preview image and snippet live on the resolved web evidence item.
 * Copy them onto the web citation with the same url (https images only),
 * keeping any value the citation already has.
 */
function withWebEvidenceImages(
  webCitations: Array<ThreadWebCitation>,
  resolved: ReadonlyArray<unknown>,
): Array<ThreadWebCitation> {
  const byUrl = new Map<string, { imageUrl?: string; snippet?: string }>()
  for (const item of resolved) {
    if (!isCitationLike(item) || item.kind !== 'web' || typeof item.url !== 'string') {
      continue
    }
    const url = item.url.trim()
    const existing = byUrl.get(url) ?? {}
    const imageUrl = existing.imageUrl ?? httpsImageSrc(item.imageUrl) ?? undefined
    const snippet = existing.snippet ?? (hasText(item.snippet) ? item.snippet.trim() : undefined)
    byUrl.set(url, { imageUrl, snippet })
  }
  if (byUrl.size === 0) {
    return webCitations
  }
  return webCitations.map((citation) => {
    const found = byUrl.get(citation.url)
    if (!found) {
      return citation
    }
    const imageUrl = citation.imageUrl ?? found.imageUrl
    const snippet = citation.snippet ?? found.snippet
    return {
      ...citation,
      ...(snippet ? { snippet } : {}),
      ...(imageUrl ? { imageUrl } : {}),
    }
  })
}

/**
 * Split a turn's raw citation list by kind, BEFORE any rendering.
 *
 * This is the ONE seam both the live stream path (the `citations` / `done`
 * frame from `assistantTurnStream`) and `messageToTurn` (reload, from
 * `KnowledgeThreadMessage.citations`) must call. Neither caller should ever
 * hand a mixed array straight to a document renderer again — see the
 * defensive backstops left in `groupCitationsByDocument.ts` and
 * `injectCitationMarkers.ts` for what happens if one does anyway.
 *
 * Never throws: a non-object entry (null, a stray primitive) is dropped
 * rather than crashing the turn.
 */
export function partitionAssistantCitations(
  citations: Array<AnyThreadCitation> | null | undefined,
  metadata?: AssistantEvidenceDoneFields,
): PartitionedAssistantCitations {
  const documentCitations: Array<KnowledgeAskCitation> = []
  const legacyWebCitations: Array<ThreadWebCitation> = []

  for (const citation of citations ?? []) {
    if (!isCitationLike(citation)) {
      continue
    }
    if (isWebAssistantCitation(citation)) {
      legacyWebCitations.push(toWebCitation(citation))
      continue
    }
    documentCitations.push(citation as KnowledgeAskCitation)
  }

  if (metadata?.evidenceVersion !== 2) {
    return { documentCitations, webCitations: legacyWebCitations }
  }

  const webCitations = withWebEvidenceImages(legacyWebCitations, metadata.citations_resolved ?? [])

  const targets = (metadata.citations_resolved ?? []).map<EvidenceCitationTarget | null>((item) => {
    if (!isCitationLike(item)) {
      return null
    }
    if (item.kind === 'web') {
      if (typeof item.url !== 'string') {
        return null
      }
      try {
        const url = new URL(item.url)
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          return null
        }
      } catch {
        return null
      }
      return { kind: 'web', sourceKind: 'web', citation: item }
    }
    if (item.kind === 'calculation') {
      if (typeof item.specHash !== 'string' || typeof item.base !== 'number' || !isCitationLike(item.source)) {
        return null
      }
      return { kind: 'calculation', sourceKind: 'calculation', evidence: item }
    }
    if (item.kind === 'page_region') {
      if (typeof item.documentToken !== 'string' || typeof item.page !== 'number' || !isCitationLike(item.region)) {
        return null
      }
      return { kind: 'page_region', sourceKind: 'page_region', evidence: item, citation: regionCitation(item) }
    }
    if (item.kind === 'study') {
      if (!hasText(item.studyToken) || !hasText(item.snippet)) {
        return null
      }
      return { kind: 'study', sourceKind: 'study', evidence: item }
    }
    if (item.kind === 'room') {
      if (!hasText(item.roomToken) || !hasText(item.snippet)) {
        return null
      }
      return { kind: 'room', sourceKind: 'room', evidence: item }
    }
    if (item.kind === 'connector_record') {
      const recordKey = typeof item.recordKey === 'string' ? item.recordKey : ''
      const url = typeof item.url === 'string' ? item.url.trim() : ''
      if (!hasText(item.app) || !hasText(item.objectType) || !hasText(item.title) ||
          !hasText(item.excerpt) || !CONNECTOR_RECORD_KEY_RE.test(recordKey) ||
          (url !== '' && !isHttpsRecordUrl(url))) {
        return null
      }
      return {
        kind: 'connector_record',
        sourceKind: 'connector_record',
        evidence: item as unknown as AssistantConnectorRecordEvidence,
      }
    }
    if ((item.kind !== 'library' && item.kind !== 'social') || typeof item.documentToken !== 'string') {
      return null
    }
    let documentIndex = item.citationToken
      ? documentCitations.findIndex((citation) => citation.citationToken === item.citationToken)
      : -1
    if (documentIndex < 0) {
      documentIndex = documentCitations.findIndex((citation) =>
        citation.documentToken === item.documentToken &&
        (citation.pageNumber ?? null) === (item.page ?? null) &&
        citation.snippet === item.snippet,
      )
    }
    if (documentIndex < 0) {
      return null
    }
    if (item.kind === 'library' && typeof item.snippet === 'string' &&
        item.snippet !== documentCitations[documentIndex].snippet) {
      // A historical replacement excerpt's anchor does not establish the
      // authoritative V2 passage's location. Keep destination identity and
      // let the canonical full-passage resolver locate it on opening.
      documentCitations[documentIndex] = {
        ...documentCitations[documentIndex], snippet: item.snippet,
        pageNumber: null, pageUnknown: true, granularity: 'document',
        unitCharStart: null, unitCharEnd: null, anchorConfidence: 0, timestampLabel: null,
      }
    }
    return {
      kind: 'document', sourceKind: item.kind,
      citation: documentCitations[documentIndex], documentIndex, evidence: item,
    }
  })
  const documentDisplayIndexes: Array<number | null> = documentCitations.map(() => null)
  targets.forEach((target, index) => {
    if (target?.kind === 'document' && documentDisplayIndexes[target.documentIndex] == null) {
      documentDisplayIndexes[target.documentIndex] = index + 1
    }
  })
  return {
    documentCitations, webCitations,
    v2: { targets, answerSpans: metadata.answer_spans ?? [], documentDisplayIndexes },
  }
}
